import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import axios from "axios";
import { CalendarDays, MapPin, Mic, Ticket, Presentation, Utensils } from "lucide-react";
import { BACKEND, posterUrl, seminarWhen, priceLabel } from "@/lib/seminars";

export const publicApi = axios.create({ baseURL: `${BACKEND}/api/public` });

export function PublicShell({ children }) {
  return (
    <div className="min-h-screen bg-[#F5F1EA] text-[#1C352D]">
      <header className="border-b border-[#1C352D]/10 bg-[#F5F1EA]/90 backdrop-blur sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center gap-3">
          <img src="/logo-rdm.png" alt="" className="h-9 w-auto" onError={(e) => { e.currentTarget.style.display = "none"; }}/>
          <Link to="/seminari" className="font-semibold tracking-tight">Ramo di Mandorlo · Seminari</Link>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-5 py-10">{children}</main>
    </div>
  );
}

export function SeatsInfo({ s }) {
  if (s.seats_left == null) return null;
  if (s.seats_left === 0) return <span className="text-red-700 font-medium">Posti esauriti</span>;
  return <span>{s.seats_left} posti disponibili</span>;
}

function SeminarTile({ s, i }) {
  return (
    <Link to={`/seminari/${s.id}`} className="group block animate-in-row" style={{ animationDelay: `${i * 60}ms` }} data-testid={`public-seminar-${s.id}`}>
      <div className="aspect-[4/5] rounded-2xl overflow-hidden bg-[#1C352D]/5 border border-[#1C352D]/10 grid place-items-center">
        {s.has_poster ? <img src={posterUrl(s)} alt={s.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"/> : <Presentation size={40} className="text-[#1C352D]/30"/>}
      </div>
      <div className="mt-3">
        <div className="font-semibold text-lg leading-tight group-hover:underline">{s.name}</div>
        <div className="text-xs text-stone-600 mt-1.5 space-y-0.5">
          <div className="flex items-center gap-1.5"><CalendarDays size={12}/>{seminarWhen(s)}</div>
          {s.location && <div className="flex items-center gap-1.5"><MapPin size={12}/>{s.location}</div>}
          {s.speaker && <div className="flex items-center gap-1.5"><Mic size={12}/>{s.speaker}</div>}
          <div className="flex items-center gap-1.5"><Ticket size={12}/>{priceLabel(s.external_price)} {s.seats_left != null && <>· <SeatsInfo s={s}/></>}</div>
          {s.lunch_price > 0 && <div className="flex items-center gap-1.5"><Utensils size={12}/>Pranzo prenotabile · € {Number(s.lunch_price).toFixed(2)}</div>}
        </div>
      </div>
    </Link>
  );
}

export default function PublicSeminars() {
  const { data = [], isLoading } = useQuery({ queryKey: ["public-seminars"], queryFn: async () => (await publicApi.get("/seminars")).data });
  return (
    <PublicShell>
      <div className="max-w-2xl mb-10">
        <div className="text-[11px] uppercase tracking-[0.25em] text-[#E2725B] font-semibold">Catalogo</div>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight mt-2">Seminari aperti a tutti</h1>
        <p className="text-stone-600 mt-3">Scegli un seminario e prenota il tuo posto, anche per più persone. Ti ricontatteremo per la conferma.</p>
      </div>
      {isLoading && <div className="text-stone-500 text-sm">Caricamento…</div>}
      {!isLoading && data.length === 0 && <div className="p-10 rounded-2xl border border-dashed border-[#1C352D]/20 text-center text-stone-500" data-testid="public-seminars-empty">Al momento non ci sono seminari in programma.</div>}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="public-seminars-grid">
        {data.map((s, i) => <SeminarTile key={s.id} s={s} i={i}/>)}
      </div>
    </PublicShell>
  );
}
